import {
  ColumnDefinition,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiDataTableComponent,
  OuiDialogComponent,
  OuiDialogHeaderComponent,
  OuiIconButtonComponent,
  OuiPaginatorComponent,
  OuiServerSideDatasource as OuiDatasource,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, effect, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

type ReturnDeviceRow = {
  id: string;
  device: string;
  imei: string;
  sku: string;
  capacity: string;
  unitPrice: number;
  skuDescription: string;
  sellPrice: string;
  sellPriceAmount: number;
};

type ReviewReturnSectionData = {
  reason: string;
  comments: string;
  attachments: Array<{
    name: string;
    type: string;
    size: string;
  }>;
  totalCreditValue: string;
  devices: Array<{
    id: string;
    device: string;
    imei: string;
    sku: string;
      capacity: string;
      unitPrice: number;
      skuDescription: string;
    sellPrice: string;
    creditValue: string;
  }>;
};

@Component({
  selector: 'app-rma-return-section',
  standalone: true,
  imports: [
    CommonModule,
    OuiBoxComponent,
    OuiButtonComponent,
    OuiDataTableComponent,
    OuiDialogComponent,
    OuiDialogHeaderComponent,
    OuiIconButtonComponent,
    OuiPaginatorComponent,
    OuiTextComponent,
    ReactiveFormsModule,
  ],
  styleUrl: './rma-return-section.component.css',
  template: `
    <div class="start-return-block">
      <div class="start-return-header" box-layout paddingBottom="sm">
        <Text role="headline-small">Return #{{ returnNumber }}</Text>
        @if (showRemoveAction) {
          <button
            text-button
            variant="ghost"
            size="sm"
            symbolLeft="delete"
            class="start-return-remove-button"
            (click)="requestRemove()"
          >
            Remove
          </button>
        }
      </div>

      <div box-layout paddingBottom="sm">
        <span class="start-return-label">Return Reason <span class="required-marker">*</span></span>
        <div class="reason-dropdown">
          <input
            class="reason-dropdown-input"
            [class.reason-dropdown-input--invalid]="reasonForm.controls.reason.invalid && reasonForm.controls.reason.touched"
            type="text"
            [value]="reasonForm.controls.reason.value || ''"
            (focus)="openReasonDropdown()"
            (input)="onReasonInputChange($any($event.target).value)"
            (blur)="handleReasonBlur()"
            placeholder="Select or search reason..."
            aria-label="Return reason"
            autocomplete="off"
          />
          <span class="reason-dropdown-arrow" aria-hidden="true"></span>

          @if (reasonForm.controls.reason.invalid && reasonForm.controls.reason.touched) {
            <div class="start-return-field-error">Return reason is required.</div>
          }

          @if (reasonDropdownOpen()) {
            <div class="reason-dropdown-menu">
              @for (option of getFilteredReasonOptions(); track option.value) {
                <button
                  type="button"
                  class="reason-dropdown-option"
                  (mousedown)="selectReason(option.value)"
                >
                  {{ option.label }}
                </button>
              }

              @if (getFilteredReasonOptions().length === 0) {
                <div class="reason-dropdown-empty">No matching reasons found.</div>
              }
            </div>
          }
        </div>
      </div>

      <div class="start-return-toolbar">
        <span class="start-return-label">Selected Device</span>
        <div class="start-return-toolbar-actions">
          <span class="start-return-selected-count start-return-main-toolbar-control">
            {{ selectedDeviceIds().length }} selected
          </span>
          <button
            text-button
            variant="primary"
            size="sm"
            symbolLeft="add"
            class="start-return-add-device-button start-return-main-toolbar-control"
            (click)="openAddDevicesDialog()"
          >
            Add Device
          </button>
          @if (selectedDeviceIds().length > 0) {
            <button
              text-button
              variant="ghost"
              size="sm"
              class="start-return-dialog-clear-button start-return-main-toolbar-control"
              (click)="clearSelectedDevices()"
            >
              Clear All
            </button>
          }
        </div>
      </div>

      @if (selectedDeviceIds().length > 0) {
        <DataTable [datasource]="selectedDevicesDatasource" [columns]="selectedDeviceColumns">
          <div ngProjectAs="data-table-footer" stack-layout gap="md">
            <div grid-layout>
              <div row-layout justifyContent="space-between">
                <Text>{{ selectedDevicesDatasource.recordCount() }} Devices Selected</Text>
                <Paginator [datasource]="selectedDevicesDatasource" [initPageSize]="10" />
                <div></div>
              </div>
            </div>
          </div>
        </DataTable>
      } @else {
        <div class="start-return-empty-devices">No devices selected. Click Add Device to choose devices.</div>
        @if (showDeviceSelectionError()) {
          <div class="start-return-device-warning">Select at least one device. Device selection is mandatory.</div>
        }
      }

      @if (startReturnType === 'credit' && selectedDeviceIds().length > 0) {
        <div class="credit-values-card">
          <div class="credit-values-header">
            <span class="credit-values-header-title">Credit Values per IMEI</span>
            <div class="credit-values-header-summary">
              <span class="credit-values-header-summary-label">{{ selectedDeviceIds().length }} devices</span>
              <span class="credit-values-header-summary-value">{{ getTotalCreditValue() }}</span>
            </div>
          </div>

          <div class="credit-values-toolbar">
            <span class="credit-values-toolbar-label">Apply same value to all:</span>
            <input
              class="credit-values-input"
              type="number"
              min="0"
              step="0.01"
              [value]="bulkCreditValue()"
              (input)="onBulkCreditValueChange($any($event.target).value)"
            />
            <button
              text-button
              variant="primary"
              size="sm"
              [disable]="!canApplyBulkCreditValue()"
              (click)="applyBulkCreditValue()"
            >
              Apply to All
            </button>
          </div>

          <DataTable [datasource]="creditValuesDatasource" [columns]="creditValueColumns">
            <div ngProjectAs="data-table-footer" stack-layout gap="md">
              <div grid-layout>
                <div row-layout justifyContent="space-between">
                  <Text>{{ creditValuesDatasource.recordCount() }} Devices Found</Text>
                  <Paginator [datasource]="creditValuesDatasource" [initPageSize]="10" />
                  <div></div>
                </div>
              </div>
            </div>
          </DataTable>

          <div class="credit-values-total">
            <span>Total Credit Value</span>
            <span>{{ getTotalCreditValue() }}</span>
          </div>
        </div>
      }

      <div class="start-return-comments-grid">
        <div>
          <span class="start-return-label">Comments</span>
          <textarea
            class="start-return-textarea"
            [class.start-return-textarea--invalid]="reasonForm.controls.comments.invalid && reasonForm.controls.comments.touched"
            [formControl]="reasonForm.controls.comments"
            placeholder="Optional notes..."
          ></textarea>
          <div class="start-return-comments-footer">
            @if (reasonForm.controls.comments.hasError('maxlength') && reasonForm.controls.comments.touched) {
              <div class="start-return-field-error">Comments cannot exceed 4000 characters.</div>
            }
            <span class="start-return-character-count">
              {{ (reasonForm.controls.comments.value || '').length }} / {{ commentsMaxLength }}
            </span>
          </div>
        </div>
        <div>
          <span class="start-return-label">Attachments</span>
          <label
            class="start-return-upload-box"
            [class.start-return-upload-box--active]="isAttachmentDragActive()"
            (dragover)="handleAttachmentDragOver($event)"
            (dragleave)="handleAttachmentDragLeave($event)"
            (drop)="handleAttachmentDrop($event)"
          >
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.png,.doc,.xls"
              class="start-return-upload-file-input"
              (change)="handleAttachmentFileInputChange($event)"
            />
            <div class="start-return-upload-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path
                  d="M16.5 6.5L9 14c-1.1 1.1-1.1 2.9 0 4s2.9 1.1 4 0l8-8c1.9-1.9 1.9-5.1 0-7s-5.1-1.9-7 0L6 11"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div>Drag and drop or click to upload</div>
            <div class="start-return-upload-subtext">PDF, JPG, PNG, DOC, XLS - 25 files max, 10 MB each</div>
          </label>
          @if (attachmentErrorMessage()) {
            <div class="attachment-error-message">{{ attachmentErrorMessage() }}</div>
          }
        </div>
      </div>

      @if (selectedAttachmentFiles().length > 0) {
        <div class="attachment-list-section">
          <div class="attachment-list-header">
            <Text role="headline-small" color="on-background-variant">
              Attached Files ({{ selectedAttachmentFiles().length }})
            </Text>
            <button text-button variant="ghost" size="sm" (click)="clearAttachmentFiles()">Remove All</button>
          </div>

          <div class="attachment-grid">
            <div class="attachment-grid-header">
              <span>File Name</span>
              <span>Type</span>
              <span>Size</span>
              <span>Action</span>
            </div>
            <div class="attachment-grid-body">
              @for (file of selectedAttachmentFiles(); track file.name + '-' + file.size + '-' + file.lastModified; let index = $index) {
                <div class="attachment-grid-row">
                  <span>{{ file.name }}</span>
                  <span>{{ getAttachmentFileType(file) }}</span>
                  <span>{{ formatAttachmentFileSize(file.size) }}</span>
                  <span class="attachment-grid-action">
                    <button
                      icon-button
                      variant="ghost"
                      size="sm"
                      symbolName="delete"
                      class="attachment-remove-button"
                      aria-label="Remove attachment"
                      (click)="removeAttachmentFile(index)"
                    ></button>
                  </span>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <Dialog [show]="showDeviceDialog()" (showing)="handleDeviceDialogShowing($event)">
        <DialogHeader>Add Devices</DialogHeader>
        <div class="device-dialog-content">
          <div class="start-return-toolbar start-return-toolbar--dialog">
            <span class="start-return-label">Select Devices</span>
            <div class="start-return-toolbar-actions">
              <div class="device-search-wrapper">
                <input
                  class="device-search-input"
                  type="text"
                  [value]="deviceSearchTerm()"
                  (input)="onDeviceSearchInputChange($any($event.target).value)"
                  placeholder="Search by SKU, Serial Number, or SKU Description"
                  aria-label="Search devices"
                />
                @if (deviceSearchTerm()) {
                  <button
                    type="button"
                    class="device-search-clear-button"
                    aria-label="Clear device search"
                    title="Clear search"
                    (click)="clearDeviceSearch()"
                  >
                    &times;
                  </button>
                }
              </div>
              <button
                text-button
                variant="primary"
                size="sm"
                class="start-return-dialog-toolbar-button"
                (click)="selectAllDraftDevices()"
              >
                Select All
              </button>
              <button
                text-button
                variant="ghost"
                size="sm"
                class="start-return-dialog-toolbar-button start-return-dialog-clear-button"
                (click)="clearDraftDevices()"
              >
                Clear All
              </button>
              <span class="start-return-selected-count start-return-dialog-toolbar-control">
                {{ draftSelectedDeviceIds().length }} selected
              </span>
            </div>
          </div>
          @if (showDeviceSelectionError()) {
            <div class="device-selection-error">At least one device must be selected.</div>
          }

          @if (pendingDeviceLoad()) {
            <div class="device-grid-loader" role="status" aria-live="polite">
              <span class="device-grid-loader-spinner" aria-hidden="true"></span>
              <span>Loading devices...</span>
            </div>
          } @else {
            <DataTable
              [datasource]="returnDevicesDatasource"
              [columns]="returnDeviceColumns"
              (rowClick)="onDeviceRowClick($event)"
            >
              <div ngProjectAs="data-table-footer" stack-layout gap="md">
                <div grid-layout>
                  <div row-layout justifyContent="space-between">
                    <Text>{{ returnDevicesDatasource.recordCount() }} Devices Found</Text>
                    <Paginator [datasource]="returnDevicesDatasource" [initPageSize]="10" />
                    <div></div>
                  </div>
                </div>
              </div>
            </DataTable>
          }

          <div class="device-dialog-actions">
            <button
              text-button
              variant="ghost"
              size="sm"
              class="start-return-dialog-action-button"
              (click)="closeAddDevicesDialog()"
            >
              Cancel
            </button>
            <button
              text-button
              variant="primary"
              size="sm"
              class="start-return-dialog-action-button"
              (click)="addSelectedDevices()"
            >
              Add
            </button>
          </div>
        </div>
      </Dialog>

      <ng-template #deviceSelectionCell let-id="id">
        <div box-layout paddingHorizontal="sm" paddingVertical="sm">
          <input
            type="checkbox"
            [checked]="isDraftDeviceSelected(id)"
            [disabled]="isDeviceSelectionDisabled(id)"
            (click)="$event.stopPropagation()"
            (change)="toggleDraftDeviceSelection(id, $any($event.target).checked)"
            aria-label="Select device"
          />
        </div>
      </ng-template>

      <ng-template #deviceNameCell let-id="id" let-skuDescription="skuDescription">
        <div class="popup-device-name-cell" [class.popup-device-name-cell--disabled]="isDeviceSelectionDisabled(id)">
          <span>{{ skuDescription }}</span>
          @if (isDeviceSelectionDisabled(id)) {
            <span class="popup-device-disabled-label">Already in RMA</span>
          }
        </div>
      </ng-template>

      <ng-template #popupImeiCell let-id="id" let-imei="imei">
        <span class="popup-cell-value" [class.popup-cell-value--disabled]="isDeviceSelectionDisabled(id)">
          {{ imei }}
        </span>
      </ng-template>

      <ng-template #popupSkuCell let-id="id" let-sku="sku">
        <span class="popup-cell-value" [class.popup-cell-value--disabled]="isDeviceSelectionDisabled(id)">
          {{ sku }}
        </span>
      </ng-template>

      <ng-template #popupSellPriceCell let-id="id" let-sellPrice="sellPrice">
        <span
          class="popup-cell-value popup-sell-price-value"
          [class.popup-cell-value--disabled]="isDeviceSelectionDisabled(id)"
        >
          {{ sellPrice }}
        </span>
      </ng-template>

      <ng-template #creditValueCell let-id="id">
        <div class="credit-value-cell" paddingVertical="sm">
          <span class="credit-value-currency">$</span>
          <input
            class="credit-values-input"
            [class.credit-values-input--error]="!!getCreditValidationMessage(id)"
            type="number"
            min="0"
            step="0.01"
            [value]="getCreditValue(id)"
            (input)="onCreditValueChange(id, $any($event.target).value)"
          />
        </div>
        @if (getCreditValidationMessage(id)) {
          <div class="credit-value-error" paddingBottom="sm">
            {{ getCreditValidationMessage(id) }}
          </div>
        }
      </ng-template>

      <ng-template #creditDeviceCell let-device="device">
        <div box-layout paddingHorizontal="sm" paddingVertical="sm">
          <span class="credit-device-primary">{{ device }}</span>
        </div>
      </ng-template>

      <ng-template #creditImeiCell let-imei="imei">
        <div box-layout paddingHorizontal="sm" paddingVertical="sm">
          <span class="credit-muted">{{ imei }}</span>
        </div>
      </ng-template>

      <ng-template #creditSellPriceCell let-sellPrice="sellPrice">
        <div box-layout paddingHorizontal="sm" paddingVertical="sm">
          <span class="credit-sell-price">{{ sellPrice }}</span>
        </div>
      </ng-template>
    </div>
  `,
  })
export class RmaReturnSectionComponent implements OnDestroy, OnChanges {
  private static readonly MIN_DEVICE_SEARCH_LENGTH = 3;
  private static readonly DEVICE_SEARCH_DEBOUNCE_MS = 1000;

  readonly deviceSelectionCell = viewChild<TemplateRef<never>>('deviceSelectionCell');
  readonly deviceNameCell = viewChild<TemplateRef<never>>('deviceNameCell');
  readonly popupImeiCell = viewChild<TemplateRef<never>>('popupImeiCell');
  readonly popupSkuCell = viewChild<TemplateRef<never>>('popupSkuCell');
  readonly popupSellPriceCell = viewChild<TemplateRef<never>>('popupSellPriceCell');
  readonly creditValueCell = viewChild<TemplateRef<never>>('creditValueCell');
  readonly creditDeviceCell = viewChild<TemplateRef<never>>('creditDeviceCell');
  readonly creditImeiCell = viewChild<TemplateRef<never>>('creditImeiCell');
  readonly creditSellPriceCell = viewChild<TemplateRef<never>>('creditSellPriceCell');

  @Input() returnNumber = 1;
  @Input() showRemoveAction = false;
  @Input() reasonOptions: Array<{ label: string; value: string }> = [];
  @Input() devicesMessage = '';
  @Input() startReturnType: 'regular' | 'credit' = 'regular';
  @Input() disabledImeis: string[] = [];
  @Output() readonly removeRequested = new EventEmitter<void>();
  @Output() readonly addDevicesRequested = new EventEmitter<void>();
  @Output() readonly deviceSearchRequested = new EventEmitter<string>();
  @Output() readonly selectedDeviceImeisChanged = new EventEmitter<string[]>();
  @Input() set deviceRows(value: ReturnDeviceRow[]) {
    const rows = value || [];
    this._deviceRows.set(rows);
    this.pendingDeviceLoad.set(false);

    this.deviceRowsById.update((current) => {
      const next = { ...current };
      for (const row of rows) {
        next[row.id] = row;
      }
      return next;
    });

    this.pruneDisabledSelections();

    this.refreshTables();
  }

  readonly reasonForm = new FormGroup({
    reason: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    comments: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(4000)],
    }),
  });

  readonly showDeviceDialog = signal(false);
  readonly selectedDeviceIds = signal<string[]>([]);
  readonly draftSelectedDeviceIds = signal<string[]>([]);
  readonly creditValueByDeviceId = signal<Record<string, number>>({});
  readonly creditInputByDeviceId = signal<Record<string, string>>({});
  readonly creditValidationByDeviceId = signal<Record<string, string>>({});
  readonly bulkCreditValue = signal<string>('');
  readonly reasonDropdownOpen = signal(false);
  readonly reasonDropdownShowAll = signal(false);
  readonly selectedAttachmentFiles = signal<File[]>([]);
  readonly attachmentErrorMessage = signal('');
  readonly isAttachmentDragActive = signal(false);
  readonly attachmentAllowedFileExtensions = ['pdf', 'jpg', 'png', 'doc', 'xls'];
  readonly deviceSearchTerm = signal('');
  readonly showDeviceSelectionError = signal(false);
  readonly pendingDeviceLoad = signal(false);
  readonly commentsMaxLength = 4000;

  private readonly maxAttachmentCount = 25;
  private readonly maxAttachmentSizeBytes = 10 * 1024 * 1024;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastRequestedSearchTerm = '';

  returnDeviceColumns: ColumnDefinition<ReturnDeviceRow>[] = [];
  selectedDeviceColumns: ColumnDefinition<ReturnDeviceRow>[] = [];
  creditValueColumns: ColumnDefinition<ReturnDeviceRow>[] = [];

  readonly returnDevicesDatasource = new OuiDatasource<ReturnDeviceRow>(
    async (request) => {
      const rows = this._deviceRows();
      const pageSize = request.pageSize || 10;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: rows.slice(start, end),
        totalRecords: rows.length,
        pageNumber,
        pageSize,
      };
    },
    10,
  );

  readonly creditValuesDatasource = new OuiDatasource<ReturnDeviceRow>(
    async (request) => {
      const rows = this.getSelectedDeviceRows();
      const pageSize = request.pageSize || 10;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: rows.slice(start, end),
        totalRecords: rows.length,
        pageNumber,
        pageSize,
      };
    },
    10,
  );

  readonly selectedDevicesDatasource = new OuiDatasource<ReturnDeviceRow>(
    async (request) => {
      const rows = this.getSelectedDeviceRows();
      const pageSize = request.pageSize || 10;
      const pageNumber = request.pageNumber || 1;
      const start = (pageNumber - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: rows.slice(start, end),
        totalRecords: rows.length,
        pageNumber,
        pageSize,
      };
    },
    10,
  );

  private readonly _deviceRows = signal<ReturnDeviceRow[]>([]);
  private readonly deviceRowsById = signal<Record<string, ReturnDeviceRow>>({});
  private readonly disabledImeisNormalized = signal<Set<string>>(new Set());

  constructor() {
    effect(() => {
      this.returnDeviceColumns = [
        {
          columnDef: 'id',
          header: '',
          template: this.deviceSelectionCell(),
        },
        {
          columnDef: 'device',
          header: 'Device',
          template: this.deviceNameCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'imei',
          header: 'IMEI',
          template: this.popupImeiCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'sku',
          header: 'SKU',
          template: this.popupSkuCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'sellPrice',
          header: 'Sell Price',
          template: this.popupSellPriceCell(),
          textAlign: 'right',
        },
      ];
    });

    effect(() => {
      this.creditValueColumns = [
        {
          columnDef: 'device',
          header: 'Device',
          template: this.creditDeviceCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'imei',
          header: 'IMEI',
          template: this.creditImeiCell(),
          textAlign: 'left',
        },
        {
          columnDef: 'sku',
          header: 'SKU',
          textAlign: 'left',
        },
        {
          columnDef: 'sellPrice',
          header: 'Sell Price',
          template: this.creditSellPriceCell(),
          textAlign: 'right',
        },
        {
          columnDef: 'id',
          header: 'Credit Value',
          template: this.creditValueCell(),
          textAlign: 'right',
        },
      ];
    });

    effect(() => {
      this.selectedDeviceColumns = [
        {
          columnDef: 'device',
          header: 'Device',
          textAlign: 'left',
        },
        {
          columnDef: 'imei',
          header: 'IMEI',
          textAlign: 'left',
        },
        {
          columnDef: 'sku',
          header: 'SKU',
          textAlign: 'left',
        },
        {
          columnDef: 'sellPrice',
          header: 'Sell Price',
          textAlign: 'right',
        },
      ];
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabledImeis']) {
      this.syncDisabledImeis();
    }

    if (changes['devicesMessage']) {
      this.pendingDeviceLoad.set(false);
    }
  }

  openReasonDropdown() {
    this.reasonDropdownShowAll.set(true);
    this.reasonDropdownOpen.set(true);
  }

  requestRemove() {
    this.removeRequested.emit();
  }

  handleReasonBlur() {
    setTimeout(() => {
      this.validateReasonValue();
      this.reasonDropdownShowAll.set(false);
      this.reasonDropdownOpen.set(false);
    }, 120);
  }

  openAddDevicesDialog() {
    this.pendingDeviceLoad.set(true);
    this.addDevicesRequested.emit();
    this.draftSelectedDeviceIds.set([...this.selectedDeviceIds()]);
    this.showDeviceDialog.set(true);
    this.refreshTables();
  }

  onDeviceSearchInputChange(value: string) {
    this.deviceSearchTerm.set(value || '');
    const term = (value || '').trim();

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }

    if (term.length === 0) {
      if (this.lastRequestedSearchTerm) {
        this.pendingDeviceLoad.set(true);
        this.deviceSearchRequested.emit('');
        this.lastRequestedSearchTerm = '';
      }
      return;
    }

    if (term.length < RmaReturnSectionComponent.MIN_DEVICE_SEARCH_LENGTH) {
      return;
    }

    this.searchDebounceTimer = setTimeout(() => {
      if (term !== this.lastRequestedSearchTerm) {
        this.pendingDeviceLoad.set(true);
        this.deviceSearchRequested.emit(term);
        this.lastRequestedSearchTerm = term;
      }
      this.searchDebounceTimer = null;
    }, RmaReturnSectionComponent.DEVICE_SEARCH_DEBOUNCE_MS);
  }

  clearDeviceSearch() {
    this.resetDeviceSearch();
    this.refreshTables();
  }

  closeAddDevicesDialog() {
    this.showDeviceDialog.set(false);
    this.draftSelectedDeviceIds.set([]);
    this.resetDeviceSearch();
    this.refreshTables();
  }

  handleDeviceDialogShowing(showing: boolean) {
    this.showDeviceDialog.set(showing);
    if (!showing) {
      this.draftSelectedDeviceIds.set([]);
      this.resetDeviceSearch();
      this.refreshTables();
    }
  }

  addSelectedDevices() {
    if (this.draftSelectedDeviceIds().length === 0) {
      this.showDeviceSelectionError.set(true);
      return;
    }
    this.showDeviceSelectionError.set(false);
    this.selectedDeviceIds.set([...new Set(this.draftSelectedDeviceIds())]);
    this.pruneCreditValuesForSelectedDevices();
    this.emitSelectedDeviceImeis();
    this.showDeviceDialog.set(false);
    this.resetDeviceSearch();
    this.refreshTables();
  }

  private resetDeviceSearch() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.deviceSearchTerm.set('');
    if (this.lastRequestedSearchTerm) {
      this.pendingDeviceLoad.set(true);
      this.deviceSearchRequested.emit('');
      this.lastRequestedSearchTerm = '';
    }
    this.showDeviceSelectionError.set(false);
  }

  private emitSelectedDeviceImeis() {
    this.selectedDeviceImeisChanged.emit(
      this.getSelectedDeviceRows()
        .map((row) => row.imei)
        .filter((imei) => this.normalizeImei(imei).length > 0),
    );
  }

  private normalizeImei(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private syncDisabledImeis() {
    const normalized = new Set(
      (this.disabledImeis || [])
        .map((imei) => this.normalizeImei(imei))
        .filter((imei) => imei.length > 0 && imei !== '-'),
    );

    this.disabledImeisNormalized.set(normalized);
    this.pruneDisabledSelections();
    this.refreshTables();
  }

  private isImeiDisabled(imei: string | null | undefined): boolean {
    const normalized = this.normalizeImei(imei);
    if (!normalized || normalized === '-') {
      return false;
    }

    return this.disabledImeisNormalized().has(normalized);
  }

  isDeviceSelectionDisabled(deviceId: string): boolean {
    const row = this.deviceRowsById()[deviceId] || this._deviceRows().find((item) => item.id === deviceId);
    if (!row) {
      return false;
    }

    return this.isImeiDisabled(row.imei);
  }

  private pruneDisabledSelections() {
    const rowsById = this.deviceRowsById();

    this.selectedDeviceIds.update((ids) =>
      ids.filter((id) => {
        const row = rowsById[id];
        return row ? !this.isImeiDisabled(row.imei) : true;
      }),
    );

    this.draftSelectedDeviceIds.update((ids) =>
      ids.filter((id) => {
        const row = rowsById[id];
        return row ? !this.isImeiDisabled(row.imei) : true;
      }),
    );
  }

  private pruneCreditValuesForSelectedDevices() {
    const validIds = new Set(this.selectedDeviceIds());
    this.creditValueByDeviceId.update((current) => {
      const next: Record<string, number> = {};
      for (const [id, value] of Object.entries(current)) {
        if (validIds.has(id)) next[id] = value;
      }
      return next;
    });
    this.creditInputByDeviceId.update((current) => {
      const next: Record<string, string> = {};
      for (const [id, value] of Object.entries(current)) {
        if (validIds.has(id)) next[id] = value;
      }
      return next;
    });
    this.creditValidationByDeviceId.update((current) => {
      const next: Record<string, string> = {};
      for (const [id, value] of Object.entries(current)) {
        if (validIds.has(id)) next[id] = value;
      }
      return next;
    });
  }

  toggleDraftDeviceSelection(deviceId: string, checked: boolean) {
    if (this.isDeviceSelectionDisabled(deviceId)) {
      return;
    }

    const current = this.draftSelectedDeviceIds();
    const next = checked
      ? [...new Set([...current, deviceId])]
      : current.filter((id) => id !== deviceId);

    this.draftSelectedDeviceIds.set(next);
    this.refreshTables();
  }

  onDeviceRowClick(row: ReturnDeviceRow) {
    if (this.isDeviceSelectionDisabled(row.id)) {
      return;
    }

    const isSelected = this.isDraftDeviceSelected(row.id);
    this.toggleDraftDeviceSelection(row.id, !isSelected);
  }

  onReasonInputChange(value: string) {
    this.reasonForm.controls.reason.setValue(value || '');
    this.reasonDropdownShowAll.set(false);
    this.reasonDropdownOpen.set(true);
  }

  selectReason(value: string) {
    this.reasonForm.controls.reason.setValue(value);
    this.reasonDropdownShowAll.set(false);
    this.reasonDropdownOpen.set(false);
  }

  getFilteredReasonOptions(): Array<{ label: string; value: string }> {
    if (this.reasonDropdownShowAll()) {
      return this.reasonOptions;
    }

    const term = (this.reasonForm.controls.reason.value || '').trim().toLowerCase();
    if (!term) {
      return this.reasonOptions;
    }

    return this.reasonOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term),
    );
  }

  validateReasonValue() {
    const value = (this.reasonForm.controls.reason.value || '').trim();
    if (!value) {
      this.reasonForm.controls.reason.setValue('');
      return;
    }

    const isValid = this.reasonOptions.some((option) => option.value === value);
    if (!isValid) {
      this.reasonForm.controls.reason.setValue('');
    }
  }

  isDraftDeviceSelected(deviceId: string): boolean {
    return this.draftSelectedDeviceIds().includes(deviceId);
  }

  selectAllDraftDevices() {
    this.draftSelectedDeviceIds.set(
      this._deviceRows()
        .filter((row) => !this.isImeiDisabled(row.imei))
        .map((row) => row.id),
    );
    this.refreshTables();
  }

  clearDraftDevices() {
    this.draftSelectedDeviceIds.set([]);
    this.refreshTables();
  }

  clearSelectedDevices() {
    this.selectedDeviceIds.set([]);
    this.draftSelectedDeviceIds.set([]);
    this.creditInputByDeviceId.set({});
    this.creditValueByDeviceId.set({});
    this.creditValidationByDeviceId.set({});
    this.bulkCreditValue.set('');
    this.emitSelectedDeviceImeis();
    this.refreshTables();
  }

  getSelectedDeviceRows(): ReturnDeviceRow[] {
    const rowsById = this.deviceRowsById();
    return this.selectedDeviceIds()
      .map((id) => rowsById[id])
      .filter((row): row is ReturnDeviceRow => !!row);
  }

  getCreditValue(deviceId: string): string {
    const inputValue = this.creditInputByDeviceId()[deviceId];
    if (typeof inputValue === 'string') {
      return inputValue;
    }

    const value = this.creditValueByDeviceId()[deviceId];
    return typeof value === 'number' ? String(value) : '';
  }

  onCreditValueChange(deviceId: string, value: string) {
    this.creditInputByDeviceId.update((current) => ({
      ...current,
      [deviceId]: value,
    }));

    if (value.trim() === '') {
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      this.creditValidationByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      this.creditValidationByDeviceId.update((current) => ({
        ...current,
        [deviceId]: 'Enter a valid non-negative amount.',
      }));
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    const maxAllowed = this.getSellPriceAmountByDeviceId(deviceId);
    if (numericValue > maxAllowed) {
      this.creditValidationByDeviceId.update((current) => ({
        ...current,
        [deviceId]: `Credit value cannot exceed selling price (${this.formatRmaValue(maxAllowed)}).`,
      }));
      this.creditValueByDeviceId.update((current) => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
      return;
    }

    this.creditValidationByDeviceId.update((current) => {
      const next = { ...current };
      delete next[deviceId];
      return next;
    });

    this.creditValueByDeviceId.update((current) => ({
      ...current,
      [deviceId]: numericValue,
    }));
  }

  onBulkCreditValueChange(value: string) {
    this.bulkCreditValue.set(value);
  }

  canApplyBulkCreditValue(): boolean {
    const rawValue = this.bulkCreditValue().trim();
    if (!rawValue) {
      return false;
    }

    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) && numericValue > 0;
  }

  applyBulkCreditValue() {
    if (!this.canApplyBulkCreditValue()) {
      return;
    }

    const numericValue = Number(this.bulkCreditValue());
    const safeValue = Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
    const selectedRows = this.getSelectedDeviceRows();
    const updates = Object.fromEntries(
      selectedRows
        .filter((row) => safeValue <= row.sellPriceAmount)
        .map((row) => [row.id, safeValue]),
    );

    this.creditInputByDeviceId.update((current) => ({
      ...current,
      ...Object.fromEntries(selectedRows.map((row) => [row.id, String(safeValue)])),
    }));

    this.creditValidationByDeviceId.update((current) => {
      const next = { ...current };
      for (const row of selectedRows) {
        if (safeValue > row.sellPriceAmount) {
          next[row.id] = `Credit value cannot exceed selling price (${this.formatRmaValue(row.sellPriceAmount)}).`;
        } else {
          delete next[row.id];
        }
      }
      return next;
    });

    this.creditValueByDeviceId.update((current) => ({
      ...current,
      ...updates,
    }));
  }

  getCreditValidationMessage(deviceId: string): string {
    return this.creditValidationByDeviceId()[deviceId] || '';
  }

  handleAttachmentFilesAttached(files: FileList | File[] | null | undefined) {
    if (!files || files.length === 0) {
      return;
    }

    const currentFiles = this.selectedAttachmentFiles();
    const nextFiles = [...currentFiles];
    const validationErrors: string[] = [];
    const incomingFiles = Array.from(files);

    for (const file of incomingFiles) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!this.attachmentAllowedFileExtensions.includes(extension)) {
        validationErrors.push(`${file.name}: unsupported file format.`);
        continue;
      }

      if (file.size > this.maxAttachmentSizeBytes) {
        validationErrors.push(`${file.name}: file size exceeds 10MB.`);
        continue;
      }

      const alreadyAttached = nextFiles.some((existingFile) =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified,
      );

      if (alreadyAttached) {
        continue;
      }

      if (nextFiles.length >= this.maxAttachmentCount) {
        validationErrors.push(
          `A maximum of ${this.maxAttachmentCount} attachments is allowed per return. Extra files were not attached.`,
        );
        break;
      }

      nextFiles.push(file);
    }

    this.selectedAttachmentFiles.set(nextFiles);
    this.attachmentErrorMessage.set(validationErrors.join(' '));
  }

  handleAttachmentFileInputChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    this.handleAttachmentFilesAttached(target?.files);

    if (target) {
      target.value = '';
    }
  }

  handleAttachmentDragOver(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(true);
  }

  handleAttachmentDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(false);
  }

  handleAttachmentDrop(event: DragEvent) {
    event.preventDefault();
    this.isAttachmentDragActive.set(false);
    this.handleAttachmentFilesAttached(event.dataTransfer?.files);
  }

  removeAttachmentFile(index: number) {
    this.selectedAttachmentFiles.update((files) => files.filter((_, currentIndex) => currentIndex !== index));
    this.attachmentErrorMessage.set('');
  }

  clearAttachmentFiles() {
    this.selectedAttachmentFiles.set([]);
    this.attachmentErrorMessage.set('');
  }

  getAttachmentFileType(file: File): string {
    const extension = file.name.split('.').pop()?.toUpperCase();
    return extension || 'UNKNOWN';
  }

  formatAttachmentFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  getTotalCreditValue(): string {
    const total = this.getSelectedDeviceRows().reduce((sum, row) => {
      const value = this.creditValueByDeviceId()[row.id] || 0;
      return sum + value;
    }, 0);

    return total.toFixed(2);
  }

  validateForReview(): boolean {
    if (this.reasonForm.controls.comments.invalid) {
      this.reasonForm.controls.comments.markAsTouched();
      return false;
    }

    this.validateReasonValue();
    const reason = this.reasonForm.controls.reason.value.trim();
    if (!reason || !this.reasonOptions.some((option) => option.value === reason)) {
      this.reasonForm.controls.reason.markAsTouched();
      return false;
    }

    const selectedRows = this.getSelectedDeviceRows();
    if (selectedRows.length === 0) {
      this.showDeviceSelectionError.set(true);
      return false;
    }

    this.showDeviceSelectionError.set(false);

    if (this.startReturnType !== 'credit') {
      return true;
    }

    this.creditValidationByDeviceId.update((current) => {
      const next = { ...current };

      for (const row of selectedRows) {
        const rawInput = this.creditInputByDeviceId()[row.id];
        const numericInput = this.creditValueByDeviceId()[row.id];
        const hasValue =
          (typeof rawInput === 'string' && rawInput.trim().length > 0) ||
          typeof numericInput === 'number';

        if (!hasValue) {
          next[row.id] = 'Credit value is required.';
          continue;
        }

        if (next[row.id] === 'Credit value is required.') {
          delete next[row.id];
        }
      }

      return next;
    });

    return !selectedRows.some((row) => !!this.creditValidationByDeviceId()[row.id]);
  }

  hasSelectedDevicesForReview(): boolean {
    return this.getSelectedDeviceRows().length > 0;
  }

  hasInvalidCreditValuesForReview(): boolean {
    return (
      this.startReturnType === 'credit' &&
      this.getSelectedDeviceRows().some((row) => !!this.creditValidationByDeviceId()[row.id])
    );
  }

  getReviewData(): ReviewReturnSectionData {
    const selectedRows = this.getSelectedDeviceRows();

    return {
      reason: (this.reasonForm.controls.reason.value || '').trim() || '-',
      comments: (this.reasonForm.controls.comments.value || '').trim() || '-',
      attachments: this.selectedAttachmentFiles().map((file) => ({
        name: file.name,
        type: this.getAttachmentFileType(file),
        size: this.formatAttachmentFileSize(file.size),
      })),
      totalCreditValue: this.getTotalCreditValue(),
      devices: selectedRows.map((row) => ({
        id: row.id,
        device: row.device,
        imei: row.imei,
        sku: row.sku,
        capacity: row.capacity,
        unitPrice: row.unitPrice,
        skuDescription: row.skuDescription,
        sellPrice: row.sellPrice,
        creditValue:
          this.startReturnType === 'credit'
            ? this.formatRmaValue(this.creditValueByDeviceId()[row.id] ?? 0)
            : '-',
      })),
    };
  }

  getAttachmentFiles(): File[] {
    return this.selectedAttachmentFiles();
  }

  ngOnDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  private getSellPriceAmountByDeviceId(deviceId: string): number {
    const row = this.deviceRowsById()[deviceId];
    return row?.sellPriceAmount ?? 0;
  }

  private formatRmaValue(value: number | null): string {
    if (value == null) {
      return '-';
    }

    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private refreshTables() {
    this.returnDevicesDatasource.setFilter({ refreshAt: Date.now() });
    this.selectedDevicesDatasource.setFilter({ refreshAt: Date.now() });
    this.creditValuesDatasource.setFilter({ refreshAt: Date.now() });
  }
}


